// API Service — centralized HTTP client with auth handling

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { AuthTokens, RefreshResponse } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Token storage keys
/**
 * The shared Guru user-action recorder's client session id rides on every
 * API call so a backend audit row (a Sage write) can be joined to the portal
 * session that made it (kanban 0419d550). Read by
 * app/middleware/portal_session.py; absent when the recorder is not loaded.
 *
 * The library keeps the id in sessionStorage under its SESSION_KEY and
 * exposes it on current().sessionId(); the storage read covers the calls a
 * page load makes before the script has loaded (same tab, same id).
 */
export const RECORDER_SESSION_HEADER = 'X-Guru-Recorder-Session';
const RECORDER_SESSION_KEY = 'guru.uiActions.sessionId';
const RECORDER_SESSION_TOKEN = /^[A-Za-z0-9_-]{1,128}$/;

export function recorderSessionId(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const api = window.GuruRecorder;
    const core = api && typeof api.current === 'function' ? api.current() : null;
    let id = core && typeof core.sessionId === 'function' ? core.sessionId() : null;
    // Before the script has loaded (the first calls of a page load) the tab's
    // id from the library's previous init is still in sessionStorage.
    if (!id) id = window.sessionStorage?.getItem(api?.SESSION_KEY || RECORDER_SESSION_KEY) ?? null;
    return id && RECORDER_SESSION_TOKEN.test(id) ? id : null;
  } catch {
    return null;
  }
}

const ACCESS_TOKEN_KEY = 'cm_access_token';
const REFRESH_TOKEN_KEY = 'cm_refresh_token';

// Token management
//
// Refresh tokens can live in localStorage (persistent — "stay logged in")
// or sessionStorage (ephemeral — cleared when browser tab closes).
// getRefreshToken checks both locations so that token auto-refresh
// works regardless of the "stay logged in" setting.
export const tokenService = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
      || sessionStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setTokens: (tokens: AuthTokens): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    tokenService.setAccessToken(tokens.access_token, tokens.expires_in);
  },

  /**
   * Store only the access token (and refresh the nginx auth_request cookie),
   * leaving the refresh token untouched. Used by the refresh flow, where
   * /auth/refresh returns a fresh access token but the same refresh token.
   */
  setAccessToken: (accessToken: string, expiresIn: number): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    // Also set a cookie for nginx auth_request (used by Metabase)
    document.cookie = `cm_auth=${accessToken}; path=/; max-age=${expiresIn}; SameSite=Lax`;
  },

  /**
   * Store the refresh token in sessionStorage only — it will be cleared
   * when the browser tab closes. Used when "stay logged in" is unchecked.
   */
  setSessionRefreshToken: (refreshToken: string): void => {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    // Clear the auth cookie
    document.cookie = 'cm_auth=; path=/; max-age=0';
  },

  hasTokens: (): boolean => {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  },
};

// ---------------------------------------------------------------------------
// Shared token refresh
// ---------------------------------------------------------------------------

// One in-flight refresh shared across callers. The axios response interceptor
// (below) and the Agent Console streaming fetch — which bypasses axios and so
// can't use the interceptor — both call refreshAccessToken(); deduping here
// means concurrent 401s trigger a single /auth/refresh round-trip.
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchange the stored refresh token for a fresh access token via /auth/refresh.
 * On success the new access token (+ cookie) is persisted and returned. Returns
 * null when refresh is impossible or fails (no refresh token, or the request
 * rejects); callers decide how to surface that (the interceptor clears tokens
 * and redirects to login). Concurrent callers share one in-flight request.
 *
 * Uses the bare `axios` (not an interceptor-wired instance) so a 401 from the
 * refresh call itself cannot recurse back into another refresh.
 */
export function refreshAccessToken(
  baseURL: string = API_BASE_URL,
): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = performTokenRefresh(baseURL).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function performTokenRefresh(baseURL: string): Promise<string | null> {
  const refreshToken = tokenService.getRefreshToken();
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<RefreshResponse>(
      `${baseURL}/auth/refresh`,
      { refresh_token: refreshToken },
    );
    tokenService.setAccessToken(data.access_token, data.expires_in);
    return data.access_token;
  } catch {
    return null;
  }
}

export interface CreateAuthedAxiosOptions {
  /**
   * URL to redirect to on irrecoverable auth failure.
   * Main app uses '/login'; copilot uses '/copilot/login'.
   */
  loginRedirectUrl: string;
  /** Base URL override (defaults to VITE_API_URL or '/api'). */
  baseURL?: string;
  /** Request timeout in ms (default 30000). */
  timeout?: number;
}

/**
 * Create an axios instance pre-wired with:
 * - request interceptor that attaches the current cm_access_token as Bearer
 * - response interceptor that, on 401:
 *   - queues concurrent requests during refresh
 *   - calls /auth/refresh with the stored refresh token
 *   - on success: stores new access token, replays the original request
 *   - on failure: clears tokens and redirects to `loginRedirectUrl`
 *
 * Each call creates its own isRefreshing/failedQueue closure so multiple
 * instances (main app + copilot) don't share state.
 */
export function createAuthedAxios(opts: CreateAuthedAxiosOptions): AxiosInstance {
  const baseURL = opts.baseURL ?? API_BASE_URL;
  const timeout = opts.timeout ?? 30000;
  const { loginRedirectUrl } = opts;

  const instance = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
    timeout,
  });

  // Request interceptor — add auth token and the recorder session
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = tokenService.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const recorderSession = recorderSessionId();
      if (recorderSession && config.headers) {
        config.headers[RECORDER_SESSION_HEADER] = recorderSession;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor — handle token refresh
  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: AxiosError) => void;
  }> = [];

  const processQueue = (error: AxiosError | null, token: string | null = null) => {
    failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else if (token) {
        promise.resolve(token);
      }
    });
    failedQueue = [];
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Skip refresh for auth endpoints — they own their own error handling
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login')
        || originalRequest.url?.includes('/auth/refresh');

      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        if (isRefreshing) {
          // Queue concurrent requests while a refresh is in flight
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(instance(originalRequest));
              },
              reject: (err: AxiosError) => reject(err),
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken(baseURL);
          if (!newToken) {
            // No refresh token, or the refresh request failed — the session is
            // unrecoverable: fail any queued requests and bounce to login.
            processQueue(error, null);
            tokenService.clearTokens();
            window.location.href = loginRedirectUrl;
            return Promise.reject(error);
          }

          processQueue(null, newToken);
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return instance(originalRequest);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

// Default main-app axios instance — redirects to /login on auth failure
const api: AxiosInstance = createAuthedAxios({ loginRedirectUrl: '/login' });

export default api;
